/**
 * PostGameReview — Star rating component for reviewing game participants.
 * Shows after a game is completed. Players can review the creator, promoter,
 * and other players. Creator/promoter can review players.
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, CheckCircle, User, Crown, Megaphone } from "lucide-react";

interface PostGameReviewProps {
  gameId: number;
  gameTitle: string;
}

function StarRating({
  rating,
  onRate,
  readonly = false,
  size = "md",
}: {
  rating: number;
  onRate?: (r: number) => void;
  readonly?: boolean;
  size?: "sm" | "md" | "lg";
}) {
  const [hover, setHover] = useState(0);
  const sizeClass = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-8 h-8" : "w-6 h-6";

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`${readonly ? "cursor-default" : "cursor-pointer"} transition-transform hover:scale-110`}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          onClick={() => onRate?.(star)}
        >
          <Star
            className={`${sizeClass} ${
              star <= (hover || rating)
                ? "fill-yellow-400 text-yellow-400"
                : "fill-transparent text-slate-600"
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
}

function RoleIcon({ role }: { role: string }) {
  switch (role) {
    case "creator":
      return <Crown className="w-4 h-4 text-yellow-400" />;
    case "promoter":
      return <Megaphone className="w-4 h-4 text-neon" />;
    default:
      return <User className="w-4 h-4 text-blue-400" />;
  }
}

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    creator: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    promoter: "bg-neon/20 text-neon border-neon/30",
    player: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  };
  return (
    <Badge variant="outline" className={`text-xs ${colors[role] || colors.player}`}>
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </Badge>
  );
}

function ReviewCard({
  participant,
  gameId,
  onReviewSubmitted,
}: {
  participant: {
    userId: number;
    name: string | null;
    role: "creator" | "promoter" | "player";
    alreadyReviewed: boolean;
  };
  gameId: number;
  onReviewSubmitted: () => void;
}) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [expanded, setExpanded] = useState(false);

  const submitReview = trpc.review.submit.useMutation({
    onSuccess: () => {
      toast.success(`Review submitted for ${participant.name || "participant"}!`);
      onReviewSubmitted();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  if (participant.alreadyReviewed) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
        <RoleIcon role={participant.role} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">
              {participant.name || "Anonymous"}
            </span>
            <RoleBadge role={participant.role} />
          </div>
        </div>
        <div className="flex items-center gap-1 text-neon">
          <CheckCircle className="w-4 h-4" />
          <span className="text-xs font-medium">Reviewed</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-3">
      <div className="flex items-center gap-3">
        <RoleIcon role={participant.role} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">
              {participant.name || "Anonymous"}
            </span>
            <RoleBadge role={participant.role} />
          </div>
        </div>
        {!expanded && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs border-neon/30 text-neon hover:bg-neon/10"
            onClick={() => setExpanded(true)}
          >
            Rate
          </Button>
        )}
      </div>

      {expanded && (
        <div className="space-y-3 pt-2 border-t border-white/10">
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">Rating:</span>
            <StarRating rating={rating} onRate={setRating} />
            {rating > 0 && (
              <span className="text-xs text-slate-400">{rating}/5</span>
            )}
          </div>
          <Textarea
            placeholder="Leave a comment (optional)..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 text-sm resize-none"
            rows={2}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={rating === 0 || submitReview.isPending}
              onClick={() =>
                submitReview.mutate({
                  gameId,
                  revieweeId: participant.userId,
                  revieweeRole: participant.role,
                  rating,
                  comment: comment.trim() || undefined,
                })
              }
              className="bg-neon text-navy hover:bg-neon/90 font-bold text-xs"
            >
              {submitReview.isPending ? "Submitting..." : "Submit Review"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setExpanded(false);
                setRating(0);
                setComment("");
              }}
              className="text-xs text-slate-400 hover:text-white"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PostGameReview({ gameId, gameTitle }: PostGameReviewProps) {
  const participants = trpc.review.getReviewableParticipants.useQuery({ gameId });
  const utils = trpc.useUtils();

  const handleReviewSubmitted = () => {
    utils.review.getReviewableParticipants.invalidate({ gameId });
    utils.review.getGameReviews.invalidate({ gameId });
  };

  if (participants.isLoading) {
    return (
      <Card className="bg-navy-800/50 border-white/10">
        <CardContent className="py-6 text-center">
          <div className="w-5 h-5 border-2 border-neon/30 border-t-neon rounded-full animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!participants.data || participants.data.length === 0) {
    return null;
  }

  const reviewed = participants.data.filter((p) => p.alreadyReviewed).length;
  const total = participants.data.length;
  const allReviewed = reviewed === total;

  return (
    <Card className="bg-navy-800/50 border-white/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" />
            Rate Participants
          </CardTitle>
          <Badge
            variant="outline"
            className={`text-xs ${
              allReviewed
                ? "bg-neon/20 text-neon border-neon/30"
                : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
            }`}
          >
            {reviewed}/{total} reviewed
          </Badge>
        </div>
        <p className="text-sm text-slate-400">
          How was your experience in "{gameTitle}"? Rate the participants to help the community.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {participants.data.map((p) => (
          <ReviewCard
            key={`${p.userId}-${p.role}`}
            participant={p}
            gameId={gameId}
            onReviewSubmitted={handleReviewSubmitted}
          />
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Compact review display for showing existing reviews.
 */
export function ReviewDisplay({
  userId,
  limit = 5,
}: {
  userId: number;
  limit?: number;
}) {
  const reviews = trpc.review.getUserReviews.useQuery({ userId, limit });

  if (reviews.isLoading || !reviews.data) return null;
  if (reviews.data.totalReviews === 0) return null;

  return (
    <Card className="bg-navy-800/50 border-white/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" />
            Reviews
          </CardTitle>
          <div className="flex items-center gap-2">
            <StarRating rating={Math.round(reviews.data.averageRating)} readonly size="sm" />
            <span className="text-sm text-slate-400">
              {reviews.data.averageRating}/5 ({reviews.data.totalReviews})
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {reviews.data.reviews.map((review) => (
          <div
            key={review.id}
            className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white">
                  {review.reviewerName || "Anonymous"}
                </span>
                <RoleBadge role={review.revieweeRole} />
              </div>
              <StarRating rating={review.rating} readonly size="sm" />
            </div>
            {review.comment && (
              <p className="text-sm text-slate-400 italic">"{review.comment}"</p>
            )}
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{review.gameTitle || "Game"}</span>
              <span>·</span>
              <span>
                {review.createdAt
                  ? new Date(review.createdAt).toLocaleDateString()
                  : ""}
              </span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export { StarRating };
